import {
  createAdminCampaignNode,
  deleteAdminCampaignNode,
  fetchAdminCampaignCosmetics,
  fetchAdminCampaignTree,
  updateAdminCampaignNode,
  type AdminCampaignNode,
} from "./campaign/api";
import { mountCampaignTree, type CampaignTreeApi } from "./campaign/tree-ui";
import {
  CAMPAIGN_MODE_OPTIONS,
  CAMPAIGN_NODE_DIAMETER_DEFAULT,
  CAMPAIGN_NODE_DIAMETER_FINAL,
  campaignModeIsRace,
  campaignModeNeedsLevel,
  type CampaignStateColors,
} from "./campaign/types";
import { mountAdminCampaignItemCreator } from "./admin-shop-creator";
import { createModalCloseButton } from "./modal-close-button";
import { fetchPenaltyLevels, fetchSpeedrunLevels } from "../lib/cloud";

export function mountAdminCampaign(root: HTMLElement) {
  root.replaceChildren();

  const toolbar = document.createElement("div");
  toolbar.className = "modeBox adminRecordsFilters";
  const addBtn = document.createElement("button");
  addBtn.type = "button";
  addBtn.className = "btn green";
  addBtn.textContent = "Add Node";
  const refreshBtn = document.createElement("button");
  refreshBtn.type = "button";
  refreshBtn.className = "btn";
  refreshBtn.textContent = "Refresh";
  const fitBtn = document.createElement("button");
  fitBtn.type = "button";
  fitBtn.className = "btn";
  fitBtn.textContent = "Fit tree";
  const expandBtn = document.createElement("button");
  expandBtn.type = "button";
  expandBtn.className = "btn";
  expandBtn.textContent = "Expand";
  expandBtn.title = "View the Campaign tree fullscreen";
  toolbar.append(addBtn, refreshBtn, fitBtn, expandBtn);
  root.appendChild(toolbar);

  const status = document.createElement("p");
  status.className = "accessHint";
  status.textContent =
    "Drag nodes to place them; corner handle to resize. Connect parents in the editor. Expand opens the tree fullscreen.";
  root.appendChild(status);

  const treeShell = document.createElement("div");
  treeShell.className = "campaignAdminTreeShell";
  const treeMount = document.createElement("div");
  treeMount.className = "campaignAdminTreeMount";
  const fsCloseBtn = document.createElement("button");
  fsCloseBtn.type = "button";
  fsCloseBtn.className = "btn campaignAdminTreeFsClose hidden";
  fsCloseBtn.textContent = "Collapse";
  fsCloseBtn.title = "Exit fullscreen tree preview";
  treeShell.append(fsCloseBtn, treeMount);
  root.appendChild(treeShell);

  const editor = document.createElement("div");
  editor.className = "modeBox adminAchievementEditor hidden";
  root.appendChild(editor);

  let treeFullscreen = false;
  const setTreeFullscreen = (on: boolean) => {
    treeFullscreen = !!on;
    treeShell.classList.toggle("campaignAdminTreeShell--fullscreen", treeFullscreen);
    document.body.classList.toggle("campaign-admin-tree-fs", treeFullscreen);
    expandBtn.textContent = treeFullscreen ? "Collapse" : "Expand";
    expandBtn.setAttribute("aria-pressed", treeFullscreen ? "true" : "false");
    fsCloseBtn.classList.toggle("hidden", !treeFullscreen);
    requestAnimationFrame(() => treeApi?.fitView());
  };
  expandBtn.onclick = () => setTreeFullscreen(!treeFullscreen);
  fsCloseBtn.onclick = () => setTreeFullscreen(false);
  const onTreeFsKey = (e: KeyboardEvent) => {
    if (e.key === "Escape" && treeFullscreen) {
      e.preventDefault();
      setTreeFullscreen(false);
    }
  };
  window.addEventListener("keydown", onTreeFsKey);

  let nodes: AdminCampaignNode[] = [];
  let treeApi: CampaignTreeApi | null = null;
  let editingId: string | null = null;
  let cosmetics: Array<{ id: string; name: string; category: string }> = [];
  let speedrunLevelOptions: Array<{ levelIndex: number; label: string }> = [];
  let golfLevelOptions: Array<{ levelIndex: number; label: string }> = [];

  let modeSelect: HTMLSelectElement;
  let levelSelect: HTMLSelectElement;
  let levelRow: HTMLDivElement;
  let layoutRefInput: HTMLInputElement;
  let layoutRow: HTMLDivElement;
  let metricSelect: HTMLSelectElement;
  let metricRow: HTMLDivElement;
  let goalInput: HTMLInputElement;
  let goalLabel: HTMLSpanElement;
  let posXInput: HTMLInputElement;
  let posYInput: HTMLInputElement;
  let parentsBox: HTMLElement;
  let parentsWrap: HTMLElement;
  let rewardDotsCheck: HTMLInputElement;
  let rewardItemCheck: HTMLInputElement;
  let rewardDotsInput: HTMLInputElement;
  let rewardDotsRow: HTMLElement;
  let rewardItemRow: HTMLElement;
  let itemAssignedLabel: HTMLSpanElement;
  let createCosmeticBtn: HTMLButtonElement;
  let clearCosmeticBtn: HTMLButtonElement;
  let activeCheck: HTMLInputElement;
  let saveBtn: HTMLButtonElement;
  let cancelBtn: HTMLButtonElement;
  let deleteBtn: HTMLButtonElement;
  let sizeValueLabel: HTMLSpanElement;
  let resetSizeBtn: HTMLButtonElement;
  let colorInput: HTMLInputElement;
  let colorLockedCheck: HTMLInputElement;
  let colorAvailableCheck: HTMLInputElement;
  let colorCompletedCheck: HTMLInputElement;
  let applyColorBtn: HTMLButtonElement;
  let resetColorsBtn: HTMLButtonElement;
  let colorSwatchHint: HTMLSpanElement;
  /** Working copy of state colors while the editor is open. */
  let draftColors: CampaignStateColors = {};
  /** Working diameter; null = default. */
  let draftDiameter: number | null = null;
  /** Assigned exclusive cosmetic for this node draft. */
  let draftCosmeticId: string | null = null;

  async function ensureLevelOptionsLoaded() {
    if (!speedrunLevelOptions.length) {
      try {
        const levels = await fetchSpeedrunLevels();
        speedrunLevelOptions = levels
          .map((l) => ({
            levelIndex: l.levelIndex,
            label: l.displayName?.trim()
              ? `${l.levelIndex} — ${l.displayName.trim()}`
              : `Level ${l.levelIndex}`,
          }))
          .sort((a, b) => a.levelIndex - b.levelIndex);
      } catch {
        speedrunLevelOptions = [];
      }
    }
    if (!golfLevelOptions.length) {
      try {
        const levels = await fetchPenaltyLevels();
        golfLevelOptions = levels
          .map((l) => ({
            levelIndex: l.levelIndex,
            label: l.displayName?.trim()
              ? `${l.levelIndex} — ${l.displayName.trim()}`
              : `Level ${l.levelIndex}`,
          }))
          .sort((a, b) => a.levelIndex - b.levelIndex);
      } catch {
        golfLevelOptions = [];
      }
    }
  }

  function syncModeDependentFields(preferredLevel?: number | null) {
    const modeId = modeSelect.value;
    const needsLevel = campaignModeNeedsLevel(modeId);
    const isRace = campaignModeIsRace(modeId);
    const isSandbox = modeId === "sandbox";

    levelRow.classList.toggle("hidden", !needsLevel);
    layoutRow.classList.toggle("hidden", !isSandbox);
    metricRow.classList.toggle("hidden", isRace);

    if (isRace) {
      metricSelect.value = "time";
      goalLabel.textContent = "Time limit (s)";
      if (!goalInput.value || Number(goalInput.value) < 1) goalInput.value = "30";
    } else {
      goalLabel.textContent = "Goal";
    }

    levelSelect.replaceChildren();
    if (needsLevel) {
      const opts = modeId === "penalty" ? golfLevelOptions : speedrunLevelOptions;
      if (!opts.length) {
        const empty = document.createElement("option");
        empty.value = "";
        empty.textContent =
          modeId === "penalty" ? "No Golf levels published" : "No SpeedRun levels published";
        levelSelect.appendChild(empty);
        levelSelect.value = "";
      } else {
        for (const o of opts) {
          const opt = document.createElement("option");
          opt.value = String(o.levelIndex);
          opt.textContent = o.label;
          levelSelect.appendChild(opt);
        }
        const pref = Math.floor(Number(preferredLevel) || 0);
        if (pref >= 1 && opts.some((o) => o.levelIndex === pref)) {
          levelSelect.value = String(pref);
        } else {
          levelSelect.value = String(opts[0]!.levelIndex);
        }
      }
    } else {
      levelSelect.value = "";
    }
  }

  function syncColorSwatchHint() {
    const parts = [
      `Locked: ${draftColors.locked || "default"}`,
      `Available: ${draftColors.available || "default"}`,
      `Completed: ${draftColors.completed || "default"}`,
    ];
    colorSwatchHint.textContent = parts.join(" · ");
  }

  function syncSizeLabel(isFinal?: boolean) {
    const d =
      draftDiameter != null
        ? draftDiameter
        : isFinal
          ? CAMPAIGN_NODE_DIAMETER_FINAL
          : CAMPAIGN_NODE_DIAMETER_DEFAULT;
    sizeValueLabel.textContent =
      draftDiameter == null
        ? `Default (${d}px) — drag the corner handle on the tree to resize`
        : `${d}px`;
  }

  function syncRewardRows() {
    rewardDotsRow.classList.toggle("hidden", !rewardDotsCheck.checked);
    rewardItemRow.classList.toggle("hidden", !rewardItemCheck.checked);
  }

  function syncAssignedItemLabel() {
    if (!draftCosmeticId) {
      itemAssignedLabel.textContent = "No exclusive item assigned yet.";
      clearCosmeticBtn.classList.add("hidden");
      return;
    }
    const found = cosmetics.find((c) => c.id === draftCosmeticId);
    itemAssignedLabel.textContent = found
      ? `Assigned: ${found.name} (${found.category})`
      : `Assigned item id: ${draftCosmeticId}`;
    clearCosmeticBtn.classList.remove("hidden");
  }

  function openExclusiveItemCreator() {
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen campaignAdminItemCreatorModal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox campaignAdminItemCreatorPanel";
    const title = document.createElement("h2");
    title.textContent = "Create exclusive Campaign item";
    const mount = document.createElement("div");
    mount.className = "campaignAdminItemCreatorMount";
    const close = () => {
      mount.replaceChildren();
      modal.remove();
    };
    box.append(createModalCloseButton(close), title, mount);
    modal.appendChild(box);
    document.body.appendChild(modal);
    mountAdminCampaignItemCreator(mount, {
      permissions: { publish: true, overwrite: false },
      unlockCampaignNodeId: editingId,
      onCampaignItemPublished: (created) => {
        draftCosmeticId = created.id;
        rewardItemCheck.checked = true;
        cosmetics = [
          { id: created.id, name: created.name, category: created.category },
          ...cosmetics.filter((c) => c.id !== created.id),
        ];
        syncRewardRows();
        syncAssignedItemLabel();
        status.textContent = `Exclusive item “${created.name}” ready — Save the node to attach it.`;
        close();
      },
    });
  }

  function fillParentsSelect(excludeId: string | null, selected: string[]) {
    parentsBox.replaceChildren();
    const noneLabel = document.createElement("label");
    noneLabel.className = "campaignAdminParentOpt";
    const noneCheck = document.createElement("input");
    noneCheck.type = "checkbox";
    noneCheck.value = "";
    noneCheck.checked = selected.length === 0;
    noneCheck.onchange = () => {
      if (noneCheck.checked) {
        parentsBox.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((c) => {
          if (c !== noneCheck) c.checked = false;
        });
      } else if (!selectedParentIds().length) {
        noneCheck.checked = true;
      }
    };
    noneLabel.append(noneCheck, document.createTextNode(" None"));
    parentsBox.appendChild(noneLabel);

    for (const n of nodes) {
      if (excludeId && n.id === excludeId) continue;
      const label = document.createElement("label");
      label.className = "campaignAdminParentOpt";
      const check = document.createElement("input");
      check.type = "checkbox";
      check.value = n.id;
      check.checked = selected.includes(n.id);
      check.onchange = () => {
        if (check.checked) noneCheck.checked = false;
        else if (!selectedParentIds().length) noneCheck.checked = true;
      };
      label.append(check, document.createTextNode(` ${n.label || n.id}`));
      parentsBox.appendChild(label);
    }
  }

  function selectedParentIds(): string[] {
    return Array.from(
      parentsBox.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked'),
    )
      .map((c) => c.value)
      .filter((v) => !!v);
  }

  function createEditorControls() {
    editor.replaceChildren();

    modeSelect = document.createElement("select");
    modeSelect.className = "formInput";
    for (const m of CAMPAIGN_MODE_OPTIONS) {
      const opt = document.createElement("option");
      opt.value = m.id;
      opt.textContent = m.label;
      modeSelect.appendChild(opt);
    }

    levelSelect = document.createElement("select");
    levelSelect.className = "formInput";
    levelSelect.setAttribute("aria-label", "Level");
    levelRow = document.createElement("div");
    levelRow.className = "formRow hidden";
    levelRow.append(
      Object.assign(document.createElement("span"), { className: "formLabel", textContent: "Level" }),
      levelSelect,
    );

    layoutRefInput = document.createElement("input");
    layoutRefInput.className = "formInput";
    layoutRefInput.type = "text";
    layoutRefInput.placeholder = "Cloud sandbox preset id";
    layoutRow = document.createElement("div");
    layoutRow.className = "formRow hidden";
    layoutRow.append(
      Object.assign(document.createElement("span"), {
        className: "formLabel",
        textContent: "Preset id",
      }),
      layoutRefInput,
    );

    metricSelect = document.createElement("select");
    metricSelect.className = "formInput";
    for (const m of [
      { id: "score", label: "Score (Level for Progression)" },
      { id: "time", label: "Time (seconds)" },
    ]) {
      const opt = document.createElement("option");
      opt.value = m.id;
      opt.textContent = m.label;
      metricSelect.appendChild(opt);
    }
    metricRow = document.createElement("div");
    metricRow.className = "formRow";
    metricRow.append(
      Object.assign(document.createElement("span"), { className: "formLabel", textContent: "Metric" }),
      metricSelect,
    );

    goalInput = document.createElement("input");
    goalInput.className = "formInput";
    goalInput.type = "number";
    goalInput.min = "1";
    goalInput.placeholder = "Goal value";
    goalLabel = Object.assign(document.createElement("span"), {
      className: "formLabel",
      textContent: "Goal",
    });

    posXInput = document.createElement("input");
    posXInput.className = "formInput";
    posXInput.type = "number";
    posYInput = document.createElement("input");
    posYInput.className = "formInput";
    posYInput.type = "number";

    parentsWrap = document.createElement("div");
    parentsWrap.className = "campaignAdminParentsWrap";
    const parentsHint = document.createElement("p");
    parentsHint.className = "accessHint campaignAdminParentsHint";
    parentsHint.textContent =
      "AND gate: clear every checked parent to unlock this node. Each parent draws its own line on the tree.";
    parentsBox = document.createElement("div");
    parentsBox.className = "formInput campaignAdminParents";
    parentsWrap.append(parentsHint, parentsBox);

    rewardDotsCheck = document.createElement("input");
    rewardDotsCheck.type = "checkbox";
    rewardDotsCheck.checked = true;
    const rewardDotsOpt = document.createElement("label");
    rewardDotsOpt.className = "campaignAdminParentOpt";
    rewardDotsOpt.append(rewardDotsCheck, document.createTextNode(" Dots"));

    rewardItemCheck = document.createElement("input");
    rewardItemCheck.type = "checkbox";
    const rewardItemOpt = document.createElement("label");
    rewardItemOpt.className = "campaignAdminParentOpt";
    rewardItemOpt.append(rewardItemCheck, document.createTextNode(" Item"));

    const rewardPickWrap = document.createElement("div");
    rewardPickWrap.className = "campaignAdminColorStates";
    rewardPickWrap.append(rewardDotsOpt, rewardItemOpt);
    rewardDotsCheck.onchange = syncRewardRows;
    rewardItemCheck.onchange = syncRewardRows;

    rewardDotsInput = document.createElement("input");
    rewardDotsInput.className = "formInput";
    rewardDotsInput.type = "number";
    rewardDotsInput.min = "0";
    rewardDotsInput.placeholder = "Dots on first clear";

    itemAssignedLabel = document.createElement("span");
    itemAssignedLabel.className = "accessHint";
    createCosmeticBtn = document.createElement("button");
    createCosmeticBtn.type = "button";
    createCosmeticBtn.className = "btn green";
    createCosmeticBtn.textContent = "Create exclusive item";
    createCosmeticBtn.onclick = () => openExclusiveItemCreator();
    clearCosmeticBtn = document.createElement("button");
    clearCosmeticBtn.type = "button";
    clearCosmeticBtn.className = "btn hidden";
    clearCosmeticBtn.textContent = "Clear item";
    clearCosmeticBtn.onclick = () => {
      draftCosmeticId = null;
      syncAssignedItemLabel();
    };

    activeCheck = document.createElement("input");
    activeCheck.type = "checkbox";
    activeCheck.checked = true;
    const activeLabel = document.createElement("label");
    activeLabel.className = "formLabel";
    activeLabel.append(activeCheck, document.createTextNode(" Active"));

    saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "btn green";
    saveBtn.textContent = "Save";
    cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    deleteBtn = document.createElement("button");
    deleteBtn.type = "button";
    deleteBtn.className = "btn";
    deleteBtn.textContent = "Delete";
    deleteBtn.classList.add("hidden");

    rewardDotsRow = document.createElement("div");
    rewardDotsRow.className = "formRow";
    rewardDotsRow.append(
      Object.assign(document.createElement("span"), { className: "formLabel", textContent: "Dots" }),
      rewardDotsInput,
    );

    rewardItemRow = document.createElement("div");
    rewardItemRow.className = "formRow campaignAdminItemReward";
    const itemCol = document.createElement("div");
    itemCol.style.display = "flex";
    itemCol.style.flexDirection = "column";
    itemCol.style.gap = "8px";
    itemCol.style.flex = "1";
    const itemActions = document.createElement("div");
    itemActions.className = "campaignAdminColorActions";
    itemActions.append(createCosmeticBtn, clearCosmeticBtn);
    itemCol.append(itemAssignedLabel, itemActions);
    rewardItemRow.append(
      Object.assign(document.createElement("span"), {
        className: "formLabel",
        textContent: "Item",
      }),
      itemCol,
    );

    sizeValueLabel = document.createElement("span");
    sizeValueLabel.className = "accessHint";
    resetSizeBtn = document.createElement("button");
    resetSizeBtn.type = "button";
    resetSizeBtn.className = "btn";
    resetSizeBtn.textContent = "Reset size";
    resetSizeBtn.onclick = () => {
      draftDiameter = null;
      const editing = nodes.find((n) => n.id === editingId);
      syncSizeLabel(!!editing?.isFinal);
      if (editingId) {
        const n = nodes.find((x) => x.id === editingId);
        if (n) {
          n.nodeDiameter = null;
          renderTree();
          void updateAdminCampaignNode({ id: editingId, nodeDiameter: null }).catch((e) => {
            status.textContent = e instanceof Error ? e.message : "Size reset failed";
          });
        }
      }
    };
    const sizeControls = document.createElement("div");
    sizeControls.className = "campaignAdminSizeControls";
    sizeControls.append(sizeValueLabel, resetSizeBtn);

    colorInput = document.createElement("input");
    colorInput.type = "color";
    colorInput.className = "adminShopColorInput";
    colorInput.value = "#ffffff";
    colorLockedCheck = document.createElement("input");
    colorLockedCheck.type = "checkbox";
    colorAvailableCheck = document.createElement("input");
    colorAvailableCheck.type = "checkbox";
    colorAvailableCheck.checked = true;
    colorCompletedCheck = document.createElement("input");
    colorCompletedCheck.type = "checkbox";
    const mkStateOpt = (check: HTMLInputElement, label: string) => {
      const lab = document.createElement("label");
      lab.className = "campaignAdminParentOpt";
      lab.append(check, document.createTextNode(` ${label}`));
      return lab;
    };
    applyColorBtn = document.createElement("button");
    applyColorBtn.type = "button";
    applyColorBtn.className = "btn green";
    applyColorBtn.textContent = "Apply color";
    applyColorBtn.onclick = () => {
      const hex = colorInput.value || "#ffffff";
      if (!colorLockedCheck.checked && !colorAvailableCheck.checked && !colorCompletedCheck.checked) {
        status.textContent = "Select at least one state for the color (Locked / Available / Completed).";
        return;
      }
      if (colorLockedCheck.checked) draftColors.locked = hex;
      if (colorAvailableCheck.checked) draftColors.available = hex;
      if (colorCompletedCheck.checked) draftColors.completed = hex;
      syncColorSwatchHint();
      status.textContent = "Color applied to selected states — Save to persist.";
      if (editingId) {
        const n = nodes.find((x) => x.id === editingId);
        if (n) {
          n.stateColors = { ...draftColors };
          renderTree();
        }
      }
    };
    resetColorsBtn = document.createElement("button");
    resetColorsBtn.type = "button";
    resetColorsBtn.className = "btn";
    resetColorsBtn.textContent = "Reset colors";
    resetColorsBtn.onclick = () => {
      draftColors = {};
      syncColorSwatchHint();
      if (editingId) {
        const n = nodes.find((x) => x.id === editingId);
        if (n) {
          n.stateColors = null;
          renderTree();
        }
      }
    };
    colorSwatchHint = document.createElement("span");
    colorSwatchHint.className = "accessHint";
    const colorStates = document.createElement("div");
    colorStates.className = "campaignAdminColorStates";
    colorStates.append(
      mkStateOpt(colorLockedCheck, "Locked (unavailable)"),
      mkStateOpt(colorAvailableCheck, "Available"),
      mkStateOpt(colorCompletedCheck, "Completed"),
    );
    const colorActions = document.createElement("div");
    colorActions.className = "campaignAdminColorActions";
    colorActions.append(colorInput, applyColorBtn, resetColorsBtn);
    const colorWrap = document.createElement("div");
    colorWrap.className = "campaignAdminColorWrap";
    colorWrap.append(colorActions, colorStates, colorSwatchHint);

    const row = (label: string, el: HTMLElement) => {
      const r = document.createElement("div");
      r.className = "formRow";
      r.append(Object.assign(document.createElement("span"), { className: "formLabel", textContent: label }), el);
      return r;
    };

    const goalRow = document.createElement("div");
    goalRow.className = "formRow";
    goalRow.append(goalLabel, goalInput);

    modeSelect.onchange = () => {
      syncModeDependentFields(Number(levelSelect.value) || null);
    };

    editor.append(
      row("Mode", modeSelect),
      levelRow,
      layoutRow,
      metricRow,
      goalRow,
      row("Pos X", posXInput),
      row("Pos Y", posYInput),
      row("Size", sizeControls),
      row("Color", colorWrap),
      row("Parents", parentsWrap),
      row("Reward", rewardPickWrap),
      rewardDotsRow,
      rewardItemRow,
      activeLabel,
      saveBtn,
      cancelBtn,
      deleteBtn,
    );

    saveBtn.onclick = async () => {
      const modeId = modeSelect.value;
      const needsLevel = campaignModeNeedsLevel(modeId);
      const isRace = campaignModeIsRace(modeId);
      const levelIndex = Math.floor(Number(levelSelect.value) || 0);
      if (needsLevel && levelIndex < 1) {
        status.textContent =
          modeId === "penalty"
            ? "Select a Golf level (publish one in Admin → Golf Levels first)."
            : "Select a SpeedRun level (publish one in Admin → SpeedRun Levels first).";
        return;
      }
      const layoutRef = layoutRefInput.value.trim();
      if (modeId === "sandbox" && !layoutRef) {
        status.textContent = "Sandbox nodes need a cloud preset id.";
        return;
      }
      const goalValue = Math.floor(Number(goalInput.value) || 0);
      if (goalValue < 1) {
        status.textContent = isRace ? "Time limit must be ≥ 1 second" : "Goal must be ≥ 1";
        return;
      }
      const wantDots = rewardDotsCheck.checked;
      const wantItem = rewardItemCheck.checked;
      const rewardDots = wantDots ? Math.max(0, Math.floor(Number(rewardDotsInput.value) || 0)) : 0;
      const rewardCosmeticId = wantItem ? draftCosmeticId : null;
      if (wantItem && !rewardCosmeticId) {
        status.textContent = "Create an exclusive item for the Item reward.";
        return;
      }
      if (!wantDots && !wantItem) {
        status.textContent = "Select Dots and/or Item for the node reward.";
        return;
      }
      const rewardType =
        wantDots && wantItem ? "both" : wantItem ? "item" : "dots";
      const payload: Record<string, unknown> = {
        modeId,
        metric: isRace ? "time" : metricSelect.value,
        goalValue,
        levelIndex: needsLevel ? levelIndex : null,
        layoutRef: modeId === "sandbox" ? layoutRef : null,
        posX: Number(posXInput.value) || 0,
        posY: Number(posYInput.value) || 0,
        parentIds: selectedParentIds(),
        rewardType,
        rewardDots,
        rewardCosmeticId,
        nodeDiameter: draftDiameter,
        stateColors: Object.keys(draftColors).length ? { ...draftColors } : null,
        active: activeCheck.checked,
      };
      try {
        if (editingId) {
          await updateAdminCampaignNode({ id: editingId, ...payload });
        } else {
          await createAdminCampaignNode(payload);
        }
        closeEditor();
        await refresh();
      } catch (e) {
        status.textContent = e instanceof Error ? e.message : "Save failed";
      }
    };

    cancelBtn.onclick = () => closeEditor();
    deleteBtn.onclick = async () => {
      if (!editingId) return;
      if (!confirm("Delete this Campaign node?")) return;
      try {
        await deleteAdminCampaignNode(editingId);
        closeEditor();
        await refresh();
      } catch (e) {
        status.textContent = e instanceof Error ? e.message : "Delete failed";
      }
    };
  }

  async function openEditor(node: AdminCampaignNode | null) {
    await ensureLevelOptionsLoaded();
    createEditorControls();
    editingId = node?.id ?? null;
    modeSelect.value = node?.modeId || "normal";
    metricSelect.value = node?.metric || "score";
    goalInput.value = String(
      node?.goalValue ?? (campaignModeIsRace(modeSelect.value) ? 30 : 1000),
    );
    layoutRefInput.value = node?.layoutRef || "";
    posXInput.value = String(node?.posX ?? nodes.length * 80);
    posYInput.value = String(node?.posY ?? 80 + (nodes.length % 3) * 100);
    rewardDotsInput.value = String(node?.rewardDots ?? 500);
    activeCheck.checked = node?.active !== false;
    const rt = node?.rewardType || "dots";
    const hasItem = !!node?.rewardCosmeticId || rt === "item" || rt === "both";
    rewardDotsCheck.checked = rt === "dots" || rt === "both" || !hasItem;
    rewardItemCheck.checked = hasItem;
    draftCosmeticId = node?.rewardCosmeticId || null;
    draftDiameter =
      node?.nodeDiameter != null && Number(node.nodeDiameter) >= 28
        ? Math.round(Number(node.nodeDiameter))
        : null;
    draftColors = node?.stateColors ? { ...node.stateColors } : {};
    syncSizeLabel(!!node?.isFinal);
    syncColorSwatchHint();
    if (draftColors.available) colorInput.value = draftColors.available;
    else if (draftColors.completed) colorInput.value = draftColors.completed;
    else if (draftColors.locked) colorInput.value = draftColors.locked;
    fillParentsSelect(editingId, node?.parentIds || []);
    syncAssignedItemLabel();
    syncRewardRows();
    syncModeDependentFields(node?.levelIndex ?? null);
    deleteBtn.classList.toggle("hidden", !editingId);
    editor.classList.remove("hidden");
    treeApi?.setSelectedId(editingId);
  }

  function closeEditor() {
    editingId = null;
    editor.classList.add("hidden");
    treeApi?.setSelectedId(null);
  }

  function renderTree() {
    treeApi?.destroy();
    const viewNodes = nodes.map((n) => ({
      ...n,
      state: (n.active === false ? "locked" : "available") as const,
      isFinal: !!n.isFinal,
    }));
    treeApi = mountCampaignTree({
      root: treeMount,
      nodes: viewNodes,
      editable: true,
      interactive: true,
      selectedId: editingId,
      onNodeActivate: (node) => {
        const full = nodes.find((n) => n.id === node.id) || null;
        void openEditor(full);
      },
      onNodeMoved: (nodeId, posX, posY) => {
        const n = nodes.find((x) => x.id === nodeId);
        if (n) {
          n.posX = posX;
          n.posY = posY;
        }
        void updateAdminCampaignNode({ id: nodeId, posX, posY }).catch((e) => {
          status.textContent = e instanceof Error ? e.message : "Move save failed";
        });
      },
      onNodeResized: (nodeId, nodeDiameter) => {
        const n = nodes.find((x) => x.id === nodeId);
        if (n) n.nodeDiameter = nodeDiameter;
        if (editingId === nodeId) {
          draftDiameter = nodeDiameter;
          syncSizeLabel(!!n?.isFinal);
        }
        void updateAdminCampaignNode({ id: nodeId, nodeDiameter }).catch((e) => {
          status.textContent = e instanceof Error ? e.message : "Resize save failed";
        });
      },
    });
  }

  async function refresh() {
    status.textContent = "Loading…";
    try {
      const [tree, cos] = await Promise.all([
        fetchAdminCampaignTree(),
        fetchAdminCampaignCosmetics().catch(() => []),
      ]);
      nodes = tree.nodes || [];
      cosmetics = cos;
      renderTree();
      status.textContent = `${nodes.length} node(s). Drag to move; corner handle to resize; click to edit.`;
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Load failed";
    }
  }

  addBtn.onclick = () => void openEditor(null);
  refreshBtn.onclick = () => void refresh();
  fitBtn.onclick = () => treeApi?.fitView();

  void refresh();

  return {
    refresh: () => void refresh(),
  };
}
